import { parseRichLabelText } from "./richLabelText.js";

const RICH_LABEL_MODEL_VERSION = 1;
const SCRIPT_MODES = ["sup", "sub"];
const NORMAL_SCRIPT_MODE = "normal";

function normalizeScript(script) {
  return SCRIPT_MODES.includes(script) ? script : NORMAL_SCRIPT_MODE;
}

function normalizeColor(color) {
  return typeof color === "string" && color.trim() ? color.trim() : undefined;
}

function normalizeFontFamily(fontFamily) {
  return typeof fontFamily === "string" && fontFamily.trim() ? fontFamily.trim() : undefined;
}

function normalizeFontSize(fontSize) {
  if (fontSize === undefined || fontSize === null || fontSize === "") {
    return undefined;
  }

  if (typeof fontSize === "number") {
    return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : undefined;
  }

  const match = String(fontSize).match(/\d+(?:\.\d+)?/);
  if (!match) {
    return undefined;
  }

  const parsed = Number(match[0]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function getSegmentSignature(segment) {
  return JSON.stringify({
    script: normalizeScript(segment?.script),
    color: normalizeColor(segment?.color) || null,
    fontSize: normalizeFontSize(segment?.fontSize) || null,
    fontFamily: normalizeFontFamily(segment?.fontFamily) || null,
  });
}

function normalizeSegment(segment) {
  const normalized = {
    text: String(segment?.text ?? ""),
    script: normalizeScript(segment?.script),
  };

  const color = normalizeColor(segment?.color);
  const fontSize = normalizeFontSize(segment?.fontSize);
  const fontFamily = normalizeFontFamily(segment?.fontFamily);

  if (color) normalized.color = color;
  if (fontSize) normalized.fontSize = fontSize;
  if (fontFamily) normalized.fontFamily = fontFamily;

  return normalized;
}

function normalizeRichTextLine(line = []) {
  const normalizedLine = [];

  (Array.isArray(line) ? line : []).forEach((segment) => {
    const normalized = normalizeSegment(segment);
    if (!normalized.text) return;

    const previous = normalizedLine[normalizedLine.length - 1];
    if (previous && getSegmentSignature(previous) === getSegmentSignature(normalized)) {
      previous.text += normalized.text;
    } else {
      normalizedLine.push(normalized);
    }
  });

  return normalizedLine;
}

function normalizeRichTextModel(model) {
  const sourceLines = Array.isArray(model?.lines) ? model.lines : [];

  return {
    version: RICH_LABEL_MODEL_VERSION,
    lines: sourceLines.map(normalizeRichTextLine),
  };
}

function legacyMarkupLinesToRichTextModel(lines = []) {
  return normalizeRichTextModel({
    version: RICH_LABEL_MODEL_VERSION,
    lines: (Array.isArray(lines) ? lines : []).map((line) => (
      parseRichLabelText(line).map((segment) => ({
        text: segment.text,
        script: normalizeScript(segment.script),
      }))
    )),
  });
}

function escapeLegacyText(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function richTextModelToLegacyMarkupLines(model) {
  const normalized = normalizeRichTextModel(model);

  return normalized.lines.map((line) => (
    line.map((segment) => {
      const text = escapeLegacyText(segment.text);
      const script = normalizeScript(segment.script);

      if (script === "sup" || script === "sub") {
        return `<${script}>${text}</${script}>`;
      }

      return text;
    }).join("")
  ));
}

function getRichTextModelPlainText(model) {
  const normalized = normalizeRichTextModel(model);
  return normalized.lines.map((line) => line.map((segment) => segment.text).join("")).join("\n");
}

export {
  NORMAL_SCRIPT_MODE,
  RICH_LABEL_MODEL_VERSION,
  SCRIPT_MODES,
  getRichTextModelPlainText,
  legacyMarkupLinesToRichTextModel,
  normalizeFontSize,
  normalizeRichTextLine,
  normalizeRichTextModel,
  richTextModelToLegacyMarkupLines,
};
